// customer-favorites.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, map, Observable, of, switchMap, catchError } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';
import { StorageService } from './storage';

/** ===================== API models ===================== */

export interface LastPricesRequest {
  customer_id: number;          // required
  company_id?: number;          // optional
  product_ids?: number[];       // optional; if omitted -> all cached rows for customer
}

export interface LastPriceProduct {
  id: number;
  name: string;
  default_code: string | null;
}

export interface LastPriceInvoice {
  id: number | null;
  name: string | null;
  date: string | null;          // 'YYYY-MM-DD'
}

export interface LastPriceRow {
  company_id: number;
  customer_id: number;
  product: LastPriceProduct;
  currency: string | null;
  uom: string | null;
  last_price: number;
  last_qty: number;
  invoice: LastPriceInvoice;
  last_sale_date: string | null; // 'YYYY-MM-DD'
  lifetime: {
    qty: number;                 // total qty (lifetime)
    orders: number;              // total invoices containing this product (lifetime)
    avg_qty: number;             // qty per invoice (lifetime)
  };
  avg_qty_30d: number;           // qty per invoice (30d)
  avg_qty_90d: number;           // qty per invoice (90d)
  avg_qty_365d: number;          // qty per invoice (365d)
}

export interface LastPricesResponse {
  status: 'success' | 'error';
  message?: string;
  result?: LastPriceRow[];
}

/** ===== NEW: route customers endpoint models ===== */
export interface RouteLastPricesRequest {
  company_id?: number;
}

export interface RouteLastPricesResponse {
  status: 'success' | 'error';
  message?: string;
  generated_at?: string;   // ISO UTC
  user_tz?: string;        // e.g. 'Asia/Kolkata'
  result?: LastPriceRow[];
}

export interface RebuildRequest {
  customer_id: number;          // required
  company_id?: number;          // optional
  product_ids?: number[];       // optional
}

export interface RebuildResponse {
  status: 'success' | 'error';
  message?: string;
  result?: { message: string; updated_lines: number };
}

/** ===================== Service ===================== */

@Injectable({ providedIn: 'root' })
export class CustomerFavoritesService extends BaseApiService {
  private readonly DB_NAME = 'CustomerFavoritesDB';
  private storeName(customerId: number) { return `customer-${customerId}`; }
  // Meta timestamp storage (uses same StorageService but different DB/stores)
  private readonly META_DB = 'CustomerFavoritesMetaDB';
  private metaStoreName(customerId: number) { return `customer-${customerId}-meta`; }
  private routeMetaStore = 'route-bulk-meta';

  constructor(
    http: HttpClient,
    busy: BusyService,
    private storage: StorageService<LastPriceRow>
  ) {
    super(http, busy);
  }

  /** ===================== Meta timestamp helpers ===================== */
  private setCustomerCacheTimestamp(customerId: number, ts: number): Observable<void> {
    // store as single-element array for simplicity
    return from(this.storage.putAll(this.META_DB, this.metaStoreName(customerId), [{ ts }] as unknown as LastPriceRow[])).pipe(
      map(() => void 0)
    );
  }

  private getCustomerCacheTimestamp(customerId: number): Observable<number> {
    return from(this.storage.getAll(this.META_DB, this.metaStoreName(customerId)) as unknown as Promise<{ ts: number }[]>).pipe(
      map(arr => (arr && arr[0] && arr[0].ts) ? arr[0].ts : 0)
    );
  }

  private setRouteBulkTimestamp(ts: number): Observable<void> {
    return from(this.storage.putAll(this.META_DB, this.routeMetaStore, [{ ts }] as unknown as LastPriceRow[])).pipe(map(() => void 0));
  }

  private getRouteBulkTimestamp(): Observable<number> {
    return from(this.storage.getAll(this.META_DB, this.routeMetaStore) as unknown as Promise<{ ts: number }[]>).pipe(
      map(arr => (arr && arr[0] && arr[0].ts) ? arr[0].ts : 0)
    );
  }

  /**
   * POST /v1/api/customer/last_prices
   * Fetch cached "latest invoiced price + averages" rows for a customer.
   */
  getLastPrices(payload: LastPricesRequest): Observable<LastPricesResponse> {
    const oneDayMs = 24 * 60 * 60 * 1000;
    return this.getCustomerCacheTimestamp(payload.customer_id).pipe(
      switchMap(ts => {
        const fresh = ts && (Date.now() - ts) < oneDayMs;
        // Use cache if fresh
        if (fresh) {
          return this.getCachedRows(payload.customer_id).pipe(
            map(rows => {
              const filtered = payload.product_ids && payload.product_ids.length
                ? rows.filter(r => payload.product_ids!.includes(r.product.id))
                : rows;
              return { status: 'success', result: filtered } as LastPricesResponse;
            }),
            // Handle errors by returning empty array
            catchError(() => of({ status: 'success', result: [] } as LastPricesResponse))
          );
        }

        // Refresh route bulk, fan-out cache, then serve from cache
        return this.getRouteCustomerProductMapAndCache({ company_id: payload.company_id }, false).pipe(
          switchMap(nested => {
            const mapForCustomer = nested.get(payload.customer_id);
            const asArray = mapForCustomer ? Array.from(mapForCustomer.values()) : [];
            const filtered = payload.product_ids && payload.product_ids.length
              ? asArray.filter(r => payload.product_ids!.includes(r.product.id))
              : asArray;
            return of({ status: 'success', result: filtered } as LastPricesResponse);
          }),
          // Handle errors by returning empty array
          catchError(() => of({ status: 'success', result: [] } as LastPricesResponse))
        );
      }),
      // Handle errors at the top level as well
      catchError(() => of({ status: 'success', result: [] } as LastPricesResponse))
    );
  }

  /**
   * Convenience: return array directly or throw on error.
   */
  getLastPricesStrict(payload: LastPricesRequest): Observable<LastPriceRow[]> {
    return this.getLastPrices(payload).pipe(
      map(res => {
        if (res.status === 'success' && res.result) return res.result;
        throw new Error(res.message || 'Failed to load customer favorites/prices');
      })
    );
  }

  /**
   * Convenience: return a Map<productId, LastPriceRow> for quick lookups.
   */
  getLastPriceMap(
    payload: LastPricesRequest
  ): Observable<Map<number, LastPriceRow>> {
    return this.getLastPricesStrict(payload).pipe(
      map(rows => {
        const m = new Map<number, LastPriceRow>();
        for (const r of rows) m.set(r.product.id, r);
        return m;
      })
    );
  }

  /** ===================== Caching helpers (per customer) ===================== */
  cacheRows(customerId: number, rows: LastPriceRow[]): Observable<LastPriceRow[]> {
    return from(this.storage.putAll(this.DB_NAME, this.storeName(customerId), rows));
  }

  getCachedRows(customerId: number): Observable<LastPriceRow[]> {
    return from(this.storage.getAll(this.DB_NAME, this.storeName(customerId)));
  }

  getCachedMap(customerId: number): Observable<Map<number, LastPriceRow>> {
    return this.getCachedRows(customerId).pipe(
      map(rows => {
        const m = new Map<number, LastPriceRow>();
        for (const r of rows) m.set(r.product.id, r);
        return m;
      })
    );
  }

  /** Try cache first, then fetch + cache. */
  getLastPriceMapCachedFirst(payload: LastPricesRequest): Observable<Map<number, LastPriceRow>> {
    return this.getCachedRows(payload.customer_id).pipe(
      switchMap(cached => {
        if (cached && cached.length > 0 && !payload.product_ids) {
          const mapCached = new Map<number, LastPriceRow>();
          for (const r of cached) mapCached.set(r.product.id, r);
          return of(mapCached);
        }
        return this.getLastPricesStrict(payload).pipe(
          switchMap(rows => this.cacheRows(payload.customer_id, rows).pipe(
            map(saved => {
              const m = new Map<number, LastPriceRow>();
              for (const r of saved) m.set(r.product.id, r);
              return m;
            })
          ))
        );
      })
    );
  }

  /**
   * Convenience: get a single product’s last price row (if present in cache).
   * If not found, returns undefined (does NOT throw).
   */
  getLastPriceForProduct(
    payload: Omit<LastPricesRequest, 'product_ids'> & { product_id: number }
  ): Observable<LastPriceRow | undefined> {
    const body: LastPricesRequest = {
      customer_id: payload.customer_id,
      company_id: payload.company_id,
      product_ids: [payload.product_id],
    };
    return this.getLastPricesStrict(body).pipe(
      map(rows => rows.find(r => r.product.id === payload.product_id))
    );
  }

  /**
   * POST /v1/api/customer/last_prices/rebuild
   * Rebuild/initialize cache from historical invoices for a customer.
   */
  rebuildCustomerCache(payload: RebuildRequest): Observable<RebuildResponse> {
    return this.post<RebuildResponse>('customer/last_prices/rebuild', payload);
  }

  /** ========================================================================
   *               NEW: Route customers — last prices (bulk)
   *  Endpoint: POST /v1/api/route/customers/last_prices
   * ======================================================================== */

  /** Raw call; returns timestamp metadata + rows. */
  getRouteCustomersLastPrices(
    payload: RouteLastPricesRequest,
    forced: boolean = false
  ): Observable<RouteLastPricesResponse> {
    if (!forced) {
      const oneDayMs = 24 * 60 * 60 * 1000;
      return this.getRouteBulkTimestamp().pipe(
        switchMap(ts => {
          const fresh = ts && (Date.now() - ts) < oneDayMs;
          if (fresh) {
            // Data is already in per-customer caches; return a minimal success
            return of({ status: 'success', message: 'Using cached route customer last prices' } as RouteLastPricesResponse);
          }
          return this.post<RouteLastPricesResponse>('route/customers/last_prices', payload);
        })
      );
    }
    return this.post<RouteLastPricesResponse>('route/customers/last_prices', payload);
  }

  /** Strict array (throws on error) + returns metadata alongside rows. */
  getRouteCustomersLastPricesStrict(
    payload: RouteLastPricesRequest
  ): Observable<{ generated_at: string | undefined; user_tz: string | undefined; rows: LastPriceRow[] }> {
    return this.getRouteCustomersLastPrices(payload).pipe(
      map(res => {
        if (res.status === 'success' && res.result) {
          return {
            generated_at: res.generated_at,
            user_tz: res.user_tz,
            rows: res.result
          };
        }
        throw new Error(res.message || 'Failed to load route customers last prices');
      })
    );
  }

  /**
   * Build nested lookup:
   *   Map<customerId, Map<productId, LastPriceRow>>
   */
  getRouteCustomerProductMap(
    payload: RouteLastPricesRequest
  ): Observable<Map<number, Map<number, LastPriceRow>>> {
    return this.getRouteCustomersLastPricesStrict(payload).pipe(
      map(({ rows }) => {
        const byCustomer = new Map<number, Map<number, LastPriceRow>>();
        for (const r of rows) {
          if (!byCustomer.has(r.customer_id)) {
            byCustomer.set(r.customer_id, new Map<number, LastPriceRow>());
          }
          byCustomer.get(r.customer_id)!.set(r.product.id, r);
        }
        return byCustomer;
      })
    );
  }

  /**
   * Fetch route rows, then fan-out cache per customer using your existing
   * customer-based stores (customer-<id>). Returns the same nested Map.
   */
  getRouteCustomerProductMapAndCache(
    payload: RouteLastPricesRequest,
    forced: boolean = false
  ): Observable<Map<number, Map<number, LastPriceRow>>> {
    return this.getRouteCustomersLastPrices(payload, forced).pipe(
      switchMap(res => {
        if (res.status === 'success' && res.result) {
          return of({ generated_at: res.generated_at, user_tz: res.user_tz, rows: res.result });
        }
        
        throw new Error(res.message || 'Failed to load route customers last prices');
      }),
      switchMap(({ rows }) => {
        // Group by customer
        const grouped = new Map<number, LastPriceRow[]>();
        for (const r of rows) {
          const list = grouped.get(r.customer_id) || [];
          list.push(r);
          grouped.set(r.customer_id, list);
        }

        // Write caches (in parallel)
        const writes: Observable<unknown>[] = [];
        grouped.forEach((list, custId) => {
          // cache rows and update per-customer timestamp
          writes.push(this.cacheRows(custId, list));
          writes.push(this.setCustomerCacheTimestamp(custId, Date.now()));
        });
        // update route-level timestamp
        writes.push(this.setRouteBulkTimestamp(Date.now()));

        // After caching, return nested map
        return (writes.length
          ? from(Promise.all(writes.map(o => o.toPromise()))).pipe(
              map(() => this.rowsToNestedMap(rows))
            )
          : of(this.rowsToNestedMap(rows))
        );
      })
    );
  }

  /** Helper to transform rows to Map<customerId, Map<productId, LastPriceRow>> */
  private rowsToNestedMap(rows: LastPriceRow[]): Map<number, Map<number, LastPriceRow>> {
    const byCustomer = new Map<number, Map<number, LastPriceRow>>();
    for (const r of rows) {
      if (!byCustomer.has(r.customer_id)) {
        byCustomer.set(r.customer_id, new Map<number, LastPriceRow>());
      }
      byCustomer.get(r.customer_id)!.set(r.product.id, r);
    }
    return byCustomer;
  }
}
